import type { TFunction } from 'i18next';
import { Search } from 'lucide-react';
import { type ReactNode, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { Badge } from '~/components/ui/badge';
import { Input } from '~/components/ui/input';
import { Skeleton } from '~/components/ui/skeleton';
import { useHeaderScrollOffset } from '~/hooks/use-header-scroll-offset';
import { cn } from '~/utils/cn';
import { formatDate, formatDateTime } from '~/utils/date';

import { useExportConfig } from '../hooks/use-export-mutations';
import type { ExportConfigSnapshot, ExportListItem } from '../types/export.types';

/** `skipEmptyRows` / `GOOGLE_PRODUCT_CATEGORY` → "Skip empty rows" / "Google product category". */
function humanize(code: string): string {
  const words = code
    .replace(/([a-z0-9])([A-Z])/g, '$1 $2')
    .replace(/[_-]+/g, ' ')
    .trim()
    .toLowerCase();
  return words.charAt(0).toUpperCase() + words.slice(1);
}

/** A value as one short line: lists joined, objects as `key: value` pairs. */
function shortValue(value: unknown): string {
  if (Array.isArray(value)) return value.map((v) => shortValue(v)).join(', ');
  if (value !== null && typeof value === 'object') {
    return Object.entries(value as Record<string, unknown>)
      .filter(([, v]) => v !== null && v !== undefined && v !== '')
      .map(([k, v]) => `${humanize(k)}: ${shortValue(v)}`)
      .join(' · ');
  }
  return String(value);
}

/** The row rules (export filters) as readable chips: "Succeeded only", "Score 6–10", … */
function ruleChips(filters: Record<string, unknown>, t: TFunction<'exports'>): string[] {
  const chips: string[] = [];
  if (filters.successOnly === true) chips.push(t('history.config.successOnly'));
  const min = filters.minPostScore;
  const max = filters.maxPostScore;
  if (min !== undefined || max !== undefined) {
    chips.push(
      t('history.config.score', { min: shortValue(min ?? 0), max: shortValue(max ?? 10) }),
    );
  }
  for (const [key, value] of Object.entries(filters)) {
    if (['successOnly', 'minPostScore', 'maxPostScore'].includes(key)) continue;
    if (value === null || value === undefined) continue;
    if (Array.isArray(value) && value.length === 0) continue;
    const text =
      Array.isArray(value) && value.length > 3
        ? t('history.config.valuesCount', { count: value.length })
        : shortValue(value);
    chips.push(`${humanize(key)}: ${text}`);
  }
  return chips;
}

/** One labelled line. */
function Row({ label, children }: { label: string; children: ReactNode }) {
  return (
    <>
      <dt className="text-muted-foreground pt-1 text-xs">{label}</dt>
      <dd className="text-foreground min-w-0 text-sm font-medium break-words">{children}</dd>
    </>
  );
}

/** A single value as a quiet pill — `primary` for the choice that defines the rows, `mono` for
 *  a code (the format). */
function ValueBadge({
  children,
  tone = 'neutral',
  mono = false,
}: {
  children: ReactNode;
  tone?: 'neutral' | 'primary';
  mono?: boolean;
}) {
  return (
    <Badge
      variant="outline"
      className={cn(
        'pointer-events-none max-w-full truncate rounded-md px-2 py-0.5 text-xs font-medium',
        tone === 'primary'
          ? 'bg-primary/10 text-primary border-transparent'
          : 'bg-neutral-30 text-foreground/90',
        mono && 'font-mono',
      )}
    >
      {children}
    </Badge>
  );
}

/** A block spanning both columns of a section — for a table under its labelled line. */
function FullRow({ children }: { children: ReactNode }) {
  return <div className="col-span-2">{children}</div>;
}

/** A titled group of lines. */
function Section({ title, children }: { title: string; children: ReactNode }) {
  return (
    <section className="flex flex-col gap-1.5">
      <h4 className="text-foreground border-b pb-1 text-[13px] font-semibold">{title}</h4>
      <dl className="grid grid-cols-[7rem_minmax(0,1fr)] gap-x-3 gap-y-1.5">{children}</dl>
    </section>
  );
}

function Chips({ items }: { items: readonly string[] }) {
  return (
    <span className="flex flex-wrap gap-1">
      {items.map((item, i) => (
        <Badge
          key={`${item}-${String(i)}`}
          variant="outline"
          className="bg-neutral-30 text-foreground/90 pointer-events-none max-w-full truncate rounded-md text-xs font-medium"
        >
          {item}
        </Badge>
      ))}
    </span>
  );
}

interface TableColumn {
  key: string;
  header: string;
  /** Tailwind width/alignment for the column's cells. */
  className?: string;
}

interface TableRowData {
  id: string;
  /** Lower-cased text the search matches against. */
  search: string;
  cells: Record<string, ReactNode>;
}

/**
 * A long list as a real table — header row, columns, rows — with a search box and a count,
 * scrolling inside a bounded frame (sticky header) instead of growing the drawer.
 */
function SearchableTable({
  columns,
  rows,
  countLabel,
}: {
  columns: readonly TableColumn[];
  rows: readonly TableRowData[];
  countLabel: string;
}) {
  const { t } = useTranslation('exports');
  const tableScrollRef = useRef<HTMLDivElement>(null);
  useHeaderScrollOffset(tableScrollRef);
  const [query, setQuery] = useState('');
  const q = query.trim().toLowerCase();
  const shown = q ? rows.filter((row) => row.search.includes(q)) : rows;
  return (
    <div className="flex flex-col gap-1.5">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <span className="text-muted-foreground text-xs">
          {q
            ? t('history.config.matchCount', { shown: shown.length, total: rows.length })
            : countLabel}
        </span>
        {rows.length > 6 && (
          <span className="relative w-48">
            <Search
              aria-hidden="true"
              className="text-muted-foreground pointer-events-none absolute top-1/2 left-2 size-3.5 -translate-y-1/2"
            />
            <Input
              data-testid="export-config-details-search-input"
              type="search"
              value={query}
              onChange={(e) => {
                setQuery(e.target.value);
              }}
              placeholder={t('history.config.searchTable')}
              aria-label={t('history.config.searchTable')}
              className="h-7 pl-7 text-xs"
            />
          </span>
        )}
      </div>
      <div
        ref={tableScrollRef}
        className="scroll-under-header max-h-64 overflow-auto rounded-md border"
      >
        <table
          data-testid="export-config-details-table"
          className="w-full border-separate border-spacing-0 text-xs"
        >
          <thead>
            <tr className="bg-neutral-30 text-foreground/75 sticky top-0 z-[1] text-left font-semibold">
              {columns.map((column) => (
                <th
                  key={column.key}
                  scope="col"
                  className={cn('border-b px-2.5 py-1.5 whitespace-nowrap', column.className)}
                >
                  {column.header}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {shown.map((row) => (
              <tr key={row.id} className="hover:bg-muted/40">
                {columns.map((column) => (
                  <td
                    key={column.key}
                    className={cn(
                      'max-w-0 truncate border-b px-2.5 py-1.5 align-middle',
                      column.className,
                    )}
                  >
                    {row.cells[column.key]}
                  </td>
                ))}
              </tr>
            ))}
            {shown.length === 0 && (
              <tr>
                <td
                  colSpan={columns.length}
                  className="text-muted-foreground px-2.5 py-3 text-center"
                >
                  {t('history.config.noMatch')}
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}

/** The batch selection in words: "Released · created … – …", or "3 picked batches". */
function batchSelectionText(selection: Record<string, unknown>, t: TFunction<'exports'>): string {
  const picked = Array.isArray(selection.includeBatchIds) ? selection.includeBatchIds.length : null;
  const preset = typeof selection.preset === 'string' ? selection.preset : 'RELEASED';
  const parts = [
    picked !== null
      ? t('history.config.pickedBatches', { count: picked })
      : t(`history.config.preset.${preset}`, { defaultValue: humanize(preset) }),
  ];
  const from = typeof selection.createdFrom === 'string' ? selection.createdFrom : null;
  const to = typeof selection.createdTo === 'string' ? selection.createdTo : null;
  if (from ?? to) {
    parts.push(
      t('history.config.createdBetween', {
        from: from ? formatDate(from) : '…',
        to: to ? formatDate(to) : '…',
      }),
    );
  }
  if (selection.includeArchived === true) parts.push(t('history.config.withArchived'));
  return parts.join(' · ');
}

/** Packaging in words: compression, rows per file, one folder per market. */
function packagingText(packaging: Record<string, unknown>, t: TFunction<'exports'>): string[] {
  const split = typeof packaging.splitRows === 'number' ? packaging.splitRows : null;
  const compression = typeof packaging.compression === 'string' ? packaging.compression : 'NONE';
  return [
    t(`history.config.compression.${compression}`, { defaultValue: humanize(compression) }),
    split
      ? t('history.config.rowsPerFile', { count: split.toLocaleString() })
      : t('history.config.noRowLimit'),
    packaging.groupBy === 'MARKET'
      ? t('history.config.folderPerMarket')
      : t('history.config.oneFolder'),
  ];
}

/** The snapshot as three groups: which rows, what the file looks like, how it is packaged. */
function ConfigBody({ config }: { config: ExportConfigSnapshot }) {
  const { t } = useTranslation('exports');
  const rules = ruleChips(config.filters, t);
  const options = Object.entries(config.options ?? {})
    .filter(([, v]) => v === true)
    .map(([k]) => humanize(k));
  const catalog = config.catalog;
  // `createdAt` is `null` on a snapshot recorded before the field existed — the chip just omits
  // the "as of" suffix then, rather than showing a misleading date.
  const batchRows: TableRowData[] = catalog
    ? catalog.batches.map((b) => {
        const name = b.name ?? b.batchId;
        return {
          id: b.batchId,
          search: `${name} ${b.batchId}`.toLowerCase(),
          cells: {
            name: (
              <span className="text-foreground font-medium" title={name}>
                {name}
              </span>
            ),
            created: b.createdAt ? formatDate(b.createdAt) : '—',
            id: (
              <span className="text-muted-foreground font-mono" title={b.batchId}>
                {b.batchId}
              </span>
            ),
          },
        };
      })
    : [];
  const selection = catalog
    ? ((catalog.spec?.batches as Record<string, unknown> | undefined) ?? catalog.batchSelection)
    : null;
  return (
    <div className="flex flex-col gap-4">
      <p className="text-muted-foreground bg-neutral-30 w-fit rounded-md px-2.5 py-1 text-xs">
        {config.requestedBy
          ? t('history.config.exportedBy', {
              when: formatDateTime(config.capturedAt),
              who: config.requestedBy,
            })
          : formatDateTime(config.capturedAt)}
      </p>

      <Section title={t('history.config.rowsTitle')}>
        {catalog && selection && (
          <>
            <Row label={t('history.config.batches')}>
              <ValueBadge tone="primary">{batchSelectionText(selection, t)}</ValueBadge>
            </Row>
            <Row label={t('history.config.versionRule')}>
              <ValueBadge>
                {t(`history.config.rule.${catalog.versionRule}`, {
                  defaultValue: humanize(catalog.versionRule),
                })}
              </ValueBadge>
            </Row>
          </>
        )}
        <Row label={t('history.config.rowRules')}>
          {rules.length > 0 ? <Chips items={rules} /> : t('history.config.none')}
        </Row>
        {catalog &&
          (catalog.pins > 0 || catalog.excludedSkus > 0 || catalog.excludedProducts > 0) && (
            <Row label={t('history.config.adjustments')}>
              {t('history.config.adjustmentsValue', {
                pins: catalog.pins,
                skus: catalog.excludedSkus,
                products: catalog.excludedProducts,
              })}
            </Row>
          )}
        {catalog?.narrowed && (
          <Row label={t('history.config.narrowed')}>
            <ValueBadge>{shortValue(catalog.narrowed)}</ValueBadge>
          </Row>
        )}
        {batchRows.length > 0 && (
          <FullRow>
            <SearchableTable
              columns={[
                { key: 'name', header: t('history.config.batchColumn'), className: 'w-[55%]' },
                {
                  key: 'created',
                  header: t('history.config.createdColumn'),
                  className: 'w-[22%]',
                },
                { key: 'id', header: t('history.config.batchIdColumn') },
              ]}
              rows={batchRows}
              countLabel={t('history.config.batchesCount', { count: batchRows.length })}
            />
          </FullRow>
        )}
      </Section>

      <Section title={t('history.config.fileTitle')}>
        <Row label={t('history.config.format')}>
          <ValueBadge mono>{config.format}</ValueBadge>
        </Row>
        <Row label={t('history.config.layout')}>
          <ValueBadge>
            {config.transformer
              ? `${config.transformer.name} v${String(config.transformer.version)}`
              : config.template
                ? humanize(config.template)
                : t('history.config.rawColumns')}
          </ValueBadge>
        </Row>
        {config.fields ? (
          <>
            <FullRow>
              <SearchableTable
                columns={[
                  { key: 'n', header: '#', className: 'w-12 text-right tabular-nums' },
                  { key: 'column', header: t('history.config.columnColumn') },
                ]}
                rows={config.fields.map((field, i) => ({
                  id: field,
                  search: field.toLowerCase(),
                  cells: {
                    n: <span className="text-muted-foreground">{i + 1}</span>,
                    column: (
                      <span className="font-mono" title={field}>
                        {field}
                      </span>
                    ),
                  },
                }))}
                countLabel={t('history.config.columnsCount', { count: config.fields.length })}
              />
            </FullRow>
          </>
        ) : (
          <Row label={t('history.config.columns')}>{t('history.config.defaultColumns')}</Row>
        )}
        {options.length > 0 && (
          <Row label={t('history.config.options')}>
            <Chips items={options} />
          </Row>
        )}
      </Section>

      {catalog && (
        <Section title={t('history.config.packagingTitle')}>
          <FullRow>
            <Chips items={packagingText(catalog.packaging, t)} />
          </FullRow>
        </Section>
      )}
    </div>
  );
}

interface ExportConfigDetailsProps {
  item: ExportListItem;
}

/**
 * Recent exports' per-export information: what the export ran with — rows (batches,
 * version rule, row rules, adjustments), file (format, layout, columns, options) and
 * packaging — read from the snapshot the trigger recorded. An export from before snapshots shows what its row keeps.
 */
export function ExportConfigDetails({ item }: ExportConfigDetailsProps) {
  const { t } = useTranslation('exports');
  const { data, isPending, isError } = useExportConfig(item.exportId);
  if (isPending) return <Skeleton className="h-24 w-full" />;
  if (isError) return <p className="text-error text-xs">{t('history.config.error')}</p>;
  if (!data) {
    return (
      <div className="flex flex-col gap-1.5">
        <p className="text-muted-foreground text-xs">{t('history.config.notRecorded')}</p>
        <dl className="grid grid-cols-[7rem_minmax(0,1fr)] gap-x-3 gap-y-1.5">
          {item.format && <Row label={t('history.config.format')}>{item.format}</Row>}
          <Row label={t('history.config.language')}>{item.locale ?? 'en-US'}</Row>
          {item.transformerName && (
            <Row label={t('history.config.layout')}>
              {`${item.transformerName}${item.transformerVersion != null ? ` v${String(item.transformerVersion)}` : ''}`}
            </Row>
          )}
        </dl>
      </div>
    );
  }
  return <ConfigBody config={data} />;
}
